// utils/comisionEdificiosUtil.js

/**
 * Resuelve la mejor fecha disponible para determinar el período de comisión.
 * Prioridad: facturafecha > mes_cierre (MM-YYYY) > mes+ano > hoy
 */
export const resolverFechaFactura = (factura) => {
  if (factura.facturafecha) return factura.facturafecha;
  if (factura.mes_cierre) {
    const [mes, ano] = factura.mes_cierre.split("-");
    return `${ano}-${mes}-01`;
  }
  if (factura.ano && factura.mes) {
    return `${factura.ano}-${String(factura.mes).padStart(2, "0")}-01`;
  }
  return new Date().toISOString();
};

/**
 * Calcula el porcentaje de comisión del edificio según la fecha de factura.
 * Prioridad: rango 1 > rango 2 > rango 3.
 * Si la fecha no cae en ningún rango definido, retorna 0 (configuración vencida
 * o no aplicable). Antes había un fallback silencioso a comision_3_porcentaje
 * que ocultaba contratos vencidos y producía cobros fantasma.
 */
export const calcularPorcentajeComisionEdificio = (edificio, fechaFacturaStr) => {
  if (!edificio || !fechaFacturaStr) return 0;

  const fechaFactura = new Date(fechaFacturaStr);
  if (isNaN(fechaFactura.getTime())) return 0;

  const rangos = [
    {
      inicio: edificio.comision_1_fechainicio,
      fin: edificio.comision_1_fechafin,
      porcentaje: edificio.comision_1_porcentaje || 0,
    },
    {
      inicio: edificio.comision_2_fechainicio,
      fin: edificio.comision_2_fechafin,
      porcentaje: edificio.comision_2_porcentaje || 0,
    },
    {
      inicio: edificio.comision_3_fechainicio,
      fin: edificio.comision_3_fechafin,
      porcentaje: edificio.comision_3_porcentaje || 0,
    },
  ];

  for (const rango of rangos) {
    if (rango.inicio && rango.fin) {
      const inicio = new Date(rango.inicio);
      const fin = new Date(rango.fin);
      if (fechaFactura >= inicio && fechaFactura <= fin) {
        return rango.porcentaje;
      }
    }
  }

  return 0;
};

/**
 * Calcula pantallas ajustadas del edificio en una factura (pauta)
 * Copia exacta de la lógica en CalculadorComisionEdificio
 */
export const calcularPantallasAjustadas = (edificio, factura) => {
  const { pantallas_ascensores, pantallas_espera, pantallas_circulacion, pantallas_cowork } = factura || {};
  const edificioId = String(edificio.id);

  const camposVacios = !pantallas_ascensores && !pantallas_espera && !pantallas_circulacion && !pantallas_cowork;

  if (camposVacios) {
    return (edificio.pantallas || 0) + (edificio.espera || 0) + (edificio.totems || 0) + (edificio.cowork || 0);
  }

  let total = 0;
  if (pantallas_ascensores?.split(",").includes(edificioId)) total += edificio.pantallas || 0;
  if (pantallas_espera?.split(",").includes(edificioId)) total += edificio.espera || 0;
  if (pantallas_circulacion?.split(",").includes(edificioId)) total += edificio.totems || 0;
  if (pantallas_cowork?.split(",").includes(edificioId)) total += edificio.cowork || 0;

  return total;
};

/**
 * Comisión edificios para factura PAUTA (multi-edificio)
 */
export const calcularComisionEdificioPauta = (factura, edificio, netoFacturaTotal) => {
  if (esEdificioComisionFija(edificio, resolverFechaFactura(factura))) return 0;
  const pantallasAjustadas = calcularPantallasAjustadas(edificio, factura);
  const proporcion = factura.pantallastotales > 0 ? pantallasAjustadas / factura.pantallastotales : 0;
  const porcentaje = calcularPorcentajeComisionEdificio(edificio, resolverFechaFactura(factura));
  return netoFacturaTotal * proporcion * (porcentaje / 100);
};

/**
 * Función maestra: comisión edificios total para una oportunidad
 */
/**
 * Determina si un edificio tiene comisión fija activa para una fecha dada.
 * Edificios con comisión fija no generan comisión variable mientras la fija esté vigente.
 * Si `comision_fija_fin` está definida, la fija deja de aplicar después de esa fecha
 * (la variable vuelve a habilitarse para fechas posteriores).
 */
export const esEdificioComisionFija = (edificio, fechaStr) => {
  if (!edificio?.comision_fija_monto || !edificio?.comision_fija_inicio || !fechaStr) return false;
  const fecha = new Date(fechaStr);
  const inicio = new Date(edificio.comision_fija_inicio);
  if (isNaN(fecha.getTime()) || isNaN(inicio.getTime()) || fecha < inicio) return false;
  if (edificio.comision_fija_fin) {
    const fin = new Date(edificio.comision_fija_fin);
    if (!isNaN(fin.getTime()) && fecha > fin) return false;
  }
  return true;
};

/**
 * Calcula comisiones fijas mensuales de edificios para un año dado.
 * Para cada mes, busca edificios con comisión fija activa y convierte USD → moneda local
 * usando TC del 1ro del mes.
 *
 * @param {Object} edificiosMap - { [id]: edificioCompleto }
 * @param {number} year - Año a calcular
 * @param {Function} getDolarValueFn - (fechaStr) => number (bound por país)
 * @returns {number[]} Array(12) de comisiones fijas mensuales en moneda local
 */
export const calcularComisionesFijasMensuales = (edificiosMap, year, getDolarValueFn) => {
  const valores = Array(12).fill(0);
  const edificios = Object.values(edificiosMap || {});
  for (let mes = 0; mes < 12; mes++) {
    const fechaPrimero = `${year}-${String(mes + 1).padStart(2, "0")}-01`;
    edificios.forEach((ed) => {
      if (!esEdificioComisionFija(ed, fechaPrimero)) return;
      const monto = Number(ed.comision_fija_monto) || 0;
      if (monto <= 0) return;
      const moneda = (ed.comision_fija_moneda || "USD").toUpperCase();
      valores[mes] += moneda === "USD" ? monto * getDolarValueFn(fechaPrimero) : monto;
    });
  }
  return valores;
};

/**
 * Función maestra: comisión edificios total para una oportunidad
 */
export const calcularComisionEdificiosTotal = (oportunidad, edificiosMap, netoDespuesAgencias) => {
  if (oportunidad.isBTL) {
    // BTL: suma por cada building
    const buildings = oportunidad.buildings || [];
    return buildings.reduce((total, building) => {
      const edificio = edificiosMap[building.id];
      if (!edificio) return total;
      if (esEdificioComisionFija(edificio, resolverFechaFactura(oportunidad))) return total;
      const porcentaje = calcularPorcentajeComisionEdificio(edificio, resolverFechaFactura(oportunidad));
      const netoBuilding = building.importe;
      return total + netoBuilding * (porcentaje / 100);
    }, 0);
  } else {
    // PAUTA: prorrateo por pantallas
    const edificiosParticipantes = oportunidad.edificiosparticipantes
      ? [...new Set(oportunidad.edificiosparticipantes.split(",").map(Number))]
      : [];
    return edificiosParticipantes.reduce((total, edificioId) => {
      const edificio = edificiosMap[edificioId];
      if (!edificio) return total;
      return total + calcularComisionEdificioPauta(oportunidad, edificio, netoDespuesAgencias);
    }, 0);
  }
};
